import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Heart, TrendingDown } from "lucide-react";
import { listMyFavorites } from "@/lib/favorites.functions";
import { AdCard } from "@/components/AdCard";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/_authenticated/sparade")({
  head: () => ({
    meta: [
      { title: "Sparade annonser — Sellavo" },
      {
        name: "description",
        content: "Dina sparade annonser samlade, med prisändringar sedan du sparade dem.",
      },
      { property: "og:title", content: "Sparade annonser — Sellavo" },
      {
        property: "og:description",
        content: "Alla annonser du sparat, med koll på prissänkningar.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: SavedPage,
});

function SavedPage() {
  const { data, isLoading } = useQuery({
    queryKey: ["my-favorites"],
    queryFn: () => listMyFavorites(),
  });

  const rows = data ?? [];

  return (
    <div className="mx-auto w-full max-w-5xl px-4 sm:px-6 lg:px-8 pb-16 pt-6 sm:pb-20 sm:pt-10">
      <header className="mb-6">
        <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
          <Heart className="h-6 w-6 text-primary" /> Sparade annonser
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Vi säger till när priset sänks eller när något du sparat blir sålt.
        </p>
      </header>

      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-64 animate-pulse rounded-xl border border-border bg-muted/40" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-10 text-center">
          <p className="text-sm text-muted-foreground">
            Du har inte sparat något än. Tryck på hjärtat på en annons så hamnar den här.
          </p>
          <Button asChild className="mt-4">
            <Link to="/annonser">Bläddra bland annonser</Link>
          </Button>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {rows.map((row: any) => {
            const drop =
              row.priceAtSave != null && row.ad.price != null && row.ad.price < row.priceAtSave
                ? row.priceAtSave - row.ad.price
                : 0;
            return (
              <div key={row.ad.slug} className="space-y-1">
                <AdCard ad={row.ad} />
                {drop > 0 && (
                  <p className="flex items-center gap-1 px-1 text-xs font-medium text-primary">
                    <TrendingDown className="h-3.5 w-3.5" />
                    Sänkt {drop.toLocaleString("sv-SE")} kr sedan du sparade
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
